import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ArticleCard from "@/components/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { faqs, hotWords, testimonials } from "@/mocks/site";
import { formatCount } from "@/components/article-utils";
import NewsletterForm from "@/components/NewsletterForm";

export default function Home() {
  const featured = articles.filter((a) => a.featured).slice(0, 3);
  const hot = [...articles].sort((a, b) => b.hot - a.hot).slice(0, 6);
  const latest = articles.slice(0, 6);
  const catMaps = categories.slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* hero */}
        <section className="relative overflow-hidden bg-primary-950">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url(https://readdy.ai/api/search-image?query=abstract%20warm%20terracotta%20and%20cream%20geometric%20shapes%20with%20soft%20rounded%20forms%2C%20editorial%20background%2C%20organic%20free%20flowing%20shapes%2C%20muted%20warm%20palette%2C%20stylish%20minimal%20artwork&width=1600&height=720&seq=gua-hero-bg-01&orientation=landscape)",
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-900/40 to-primary-950"></div>
          <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24 lg:py-28">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-background-50/10 px-4 py-1.5 text-sm font-medium text-accent-100 ring-1 ring-background-50/20">
                <i className="ri-sparkling-line"></i>
                今日已整理 {articles.length} 篇热门内容
              </span>
              <h1 className="mt-6 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl lg:text-[54px] lg:leading-[1.1]">
                不想漏瓜，
                <br />
                先来 <span className="text-accent-300">51瓜网</span> 资源汇总
              </h1>
              <p className="mt-5 max-w-xl text-base leading-7 text-background-100/80 md:text-lg">
                影视热剧、综艺名场面、明星动态与音乐现场，热门资源与汇总入口都集中在这里。点开即可看到资料库与整理好的栏目导航，一次看齐，不用反复跳转。
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/library"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-compass-3-line"></i>
                  进入资源汇总
                </Link>
                <Link
                  to="#channels"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 ring-1 ring-background-50/25 transition-colors hover:bg-background-50/20"
                >
                  查看栏目导航
                  <i className="ri-arrow-down-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* trending ticker */}
        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 md:px-6">
            <span className="flex shrink-0 items-center gap-1.5 text-sm font-bold text-primary-700">
              <i className="ri-fire-line text-accent-500"></i>
              吃瓜热词
            </span>
            <div className="flex flex-1 items-center gap-2 overflow-hidden">
              <div className="flex flex-wrap items-center gap-2">
                {hotWords.map((w, i) => (
                  <Link
                    key={w}
                    to="/library"
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                      i < 3
                        ? "bg-accent-100 font-semibold text-accent-900 hover:bg-accent-200"
                        : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-800"
                    }`}
                  >
                    {i < 3 && <span className="mr-1 text-accent-600">#{i + 1}</span>}
                    {w}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* channels + hot rank */}
        <section id="channels" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* left nav channels */}
            <div className="lg:col-span-1">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                  栏目导航 <span className="text-primary-600">·</span> 找你想吃的瓜
                </h2>
                <i className="ri-layout-grid-fill text-2xl text-primary-600"></i>
              </div>
              <p className="mt-2 text-sm leading-6 text-foreground-600">
                按内容方向整理好的分类入口，点击进入对应资料库，快速定位感兴趣的资讯。
              </p>
              <div className="mt-6 space-y-4">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/library?cat=${c.id}`}
                    className="group flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 hover:bg-background-100"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                      <i className={`${c.icon} text-xl`}></i>
                    </span>
                    <div className="flex-1">
                      <h3 className="font-heading text-base font-bold text-foreground-950">{c.name}</h3>
                      <p className="mt-0.5 text-xs text-foreground-500">{c.desc}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-foreground-800">{formatCount(c.count)}</p>
                      <p className="text-[11px] text-foreground-400">篇内容</p>
                    </div>
                  </Link>
                ))}
              </div>
              <Link
                to="/library"
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-background-100 px-4 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-accent-100 hover:text-accent-900"
              >
                查看全部内容
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* right featured + hot */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {featured.map((a) => (
                  <ArticleCard key={a.id} a={a} />
                ))}
              </div>

              <div className="mt-6 overflow-hidden rounded-2xl border border-background-200 bg-background-100">
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                      <i className="ri-trophy-line text-base"></i>
                    </span>
                    本周吃瓜热度榜
                  </h3>
                  <Link
                    to="/library"
                    className="flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
                  >
                    更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <ol className="grid grid-cols-1 gap-px bg-background-200 md:grid-cols-2">
                  {hot.map((a, i) => (
                    <li key={a.id} className="bg-background-50">
                      <Link
                        to={`/article/${a.id}`}
                        className="flex items-center gap-4 p-4 transition-colors hover:bg-accent-50"
                      >
                        <span
                          className={`font-heading text-2xl font-black ${
                            i === 0
                              ? "text-accent-500"
                              : i === 1
                              ? "text-primary-600"
                              : i === 2
                              ? "text-accent-600"
                              : "text-foreground-200"
                          }`}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-foreground-900">{a.title}</p>
                          <div className="mt-1 flex items-center gap-3 text-xs text-foreground-400">
                            <span>{a.catName}</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-fire-line text-primary-500"></i>
                              {a.hot}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* latest feed */}
        <section className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <div className="flex items-end justify-between">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800">
                  最新整理
                </span>
                <h2 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                  内容最新推荐
                </h2>
              </div>
              <Link
                to="/library"
                className="flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                进入资料库 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {latest.map((a) => (
                <ArticleCard key={a.id} a={a} />
              ))}
            </div>
          </div>
        </section>

        {/* category deep dives */}
        {catMaps.map((cat, ci) => {
          const list = articles.filter((a) => a.cat === cat.id).slice(0, 4);
          return (
            <section
              key={cat.id}
              className={`${ci % 2 === 1 ? "bg-accent-50" : "bg-background-50"}`}
            >
              <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
                <div className="flex items-end justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-100 text-accent-800">
                      <i className={`${cat.icon} text-2xl`}></i>
                    </span>
                    <div>
                      <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                        {cat.name}精选
                      </h2>
                      <p className="mt-0.5 text-sm text-foreground-600">{cat.desc}</p>
                    </div>
                  </div>
                  <Link
                    to={`/library?cat=${cat.id}`}
                    className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-700"
                  >
                    更多 {cat.name} <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {list.map((a) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.id}`}
                      className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
                    >
                      <div className="relative aspect-[16/11] overflow-hidden">
                        <img
                          src={a.cover}
                          alt={`${a.title} 配图`}
                          title={`${a.title} - 51瓜网`}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                          {a.title}
                        </h3>
                        <p className="mt-2 flex items-center gap-1 text-xs text-foreground-400">
                          <i className="ri-eye-line"></i>
                          {a.hot} 热度 · {a.date}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* about + newsletter */}
        <section className="bg-background-100">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-2">
            <div className="overflow-hidden rounded-3xl bg-background-50 p-7 md:p-9">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800">
                关于 51瓜网
              </span>
              <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                把零散的瓜，聚成一个入口
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                我们相信，追热点不该是一件让人疲惫的事。51瓜网把散落在不同平台的影视、综艺、明星动态与音乐资讯整理成清晰的分栏，让每个人都能用最短的时间，掌握当天值得关注的娱乐动态。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                比起追逐碎片化的标题，我们更愿意沉淀有回看价值的盘点与资料，让每一次阅读都轻松、有料、可持续。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                了解更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="flex flex-col justify-center rounded-3xl bg-primary-950 p-7 text-background-50 md:p-9">
              <h2 className="font-heading text-2xl font-extrabold md:text-3xl">
                订阅瓜讯，热门不缺席
              </h2>
              <p className="mt-3 text-sm leading-7 text-background-100/80">
                留下邮箱，我们会把最新整理的影视、综艺与热点盘点，定期推送到你的收件箱。重要瓜料，一个都不漏。
              </p>
              <div className="mt-6">
                <NewsletterForm />
              </div>
            </div>
          </div>
        </section>

        {/* testimonials */}
        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              大家怎么说
            </span>
            <h2 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              轻松吃瓜的好帮手
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure
                key={t.name}
                className="rounded-2xl border border-background-200 bg-background-100 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                <blockquote className="mt-3 text-sm leading-7 text-foreground-700">{t.text}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={`${t.name} 头像`}
                    title={`${t.name} - 51瓜网用户`}
                    className="h-11 w-11 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-accent-50">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-xs font-semibold text-foreground-700">
                  常见问题
                </span>
                <h2 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                  关于 51瓜网
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-600">
                  第一次来？这里整理了访问者最常问的几个问题，帮你更快上手。
                </p>
                <Link
                  to="/faq"
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-accent-200 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-accent-100"
                >
                  查看全部问题
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="space-y-3 lg:col-span-2">
                {faqs.slice(0, 5).map((f) => (
                  <FaqRow key={f.q} q={f.q} a={f.a} />
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function FaqRow({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-2xl border border-accent-200/70 bg-background-50">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-heading text-sm font-bold text-foreground-900 md:text-base">{q}</span>
        <i
          className={`shrink-0 text-lg text-primary-600 transition-transform ${open ? "rotate-180" : ""} ${
            open ? "ri-subtract-line" : "ri-add-line"
          }`}
        ></i>
      </button>
      {open && (
        <p className="px-5 pb-5 text-sm leading-7 text-foreground-600">{a}</p>
      )}
    </div>
  );
}